import React from 'react';

const STATUS_CONFIG = {
  queued: { label: 'Queued', color: '#9CA3AF', bgColor: '#F3F4F6', icon: '⏳' },
  uploading: { label: 'Uploading', color: '#3B82F6', bgColor: '#EFF6FF', icon: '⬆️' },
  staged: { label: 'Uploaded', color: '#3B82F6', bgColor: '#EFF6FF', icon: '✓' },
  scanning: { label: 'AI Reading...', color: '#3B82F6', bgColor: '#EFF6FF', icon: '🔍' },
  extracting: { label: 'Extracting...', color: '#3B82F6', bgColor: '#EFF6FF', icon: '📄' },
  ocr_processing: { label: 'AI Visualizing...', color: '#F59E0B', bgColor: '#FFFBEB', icon: '👁️' },
  chunking: { label: 'Organizing...', color: '#F59E0B', bgColor: '#FFFBEB', icon: '📊' },
  importing: { label: 'Sending to AI...', color: '#F59E0B', bgColor: '#FFFBEB', icon: '☁️' },
  indexing: { label: 'AI Learning...', color: '#F59E0B', bgColor: '#FFFBEB', icon: '🧠' },
  search_warming: { label: 'Almost Ready...', color: '#EAB308', bgColor: '#FEFCE8', icon: '🔥' },
  ready: { label: 'Ready', color: '#22C55E', bgColor: '#F0FDF4', icon: '✅' },
  failed: { label: 'Failed', color: '#EF4444', bgColor: '#FEF2F2', icon: '❌' },
  // Legacy status mapping
  pending: { label: 'Queued', color: '#9CA3AF', bgColor: '#F3F4F6', icon: '⏳' },
  processing: { label: 'Processing...', color: '#F59E0B', bgColor: '#FFFBEB', icon: '⚙️' },
  ocr_queued: { label: 'AI Visualizing...', color: '#F59E0B', bgColor: '#FFFBEB', icon: '👁️' },
  chunked: { label: 'Organizing...', color: '#F59E0B', bgColor: '#FFFBEB', icon: '📊' },
};

const FAILED_AT_STAGE_CONFIG = {
  uploading: {
    label: 'Upload failed',
    icon: '⬆️ ✗',
    detail: 'Could not save your file. Please try uploading again.',
  },
  staged: {
    label: 'Storage failed',
    icon: '✓ ✗',
    detail: 'File was received but could not be stored securely.',
  },
  scanning: {
    label: 'Reading failed',
    icon: '🔍 ✗',
    detail: 'AI could not read your document. The file may be corrupted or password-protected.',
  },
  extracting: {
    label: 'Extraction failed',
    icon: '📄 ✗',
    detail: 'Could not pull text from your document. Try converting to PDF first.',
  },
  ocr_processing: {
    label: 'Visual recognition failed',
    icon: '👁️ ✗',
    detail: 'AI could not recognize text in the images. The scan quality may be too low.',
  },
  chunking: {
    label: 'Organization failed',
    icon: '📊 ✗',
    detail: 'Document was read but could not be organized for search. It may be empty.',
  },
  importing: {
    label: 'Cloud sync failed',
    icon: '☁️ ✗',
    detail: 'Could not send your document to Google AI. The service may be temporarily unavailable.',
  },
  indexing: {
    label: 'AI indexing failed',
    icon: '🧠 ✗',
    detail: 'Google AI could not learn your document. Please try again.',
  },
  search_warming: {
    label: 'Search setup failed',
    icon: '🔥 ✗',
    detail: 'Document was indexed but search could not be activated. Retrying automatically...',
  },
};

export default function PipelineStatusBadge({
  status,
  failedAtStage,
  detail,
  errorCode,
  onRetry,
  showDetail = false,
}) {
  const isFailed = status === 'failed';

  if (isFailed) {
    const failConfig = FAILED_AT_STAGE_CONFIG[failedAtStage] || {
      label: 'Processing failed',
      icon: '❌',
      detail: detail || 'Something went wrong. Please try again.',
    };
    const canManualRetry = Boolean(onRetry) && failedAtStage !== 'search_warming';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 500,
            color: '#DC2626',
            backgroundColor: '#FEF2F2',
          }}
        >
          <span>{failConfig.icon}</span>
          <span>{failConfig.label}</span>
        </span>

        <div
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
          }}
        >
          <p style={{ fontSize: 12, color: '#991B1B', margin: 0, lineHeight: 1.4 }}>
            {detail || failConfig.detail}
          </p>

          {errorCode && (
            <p
              style={{
                fontSize: 10,
                color: '#9CA3AF',
                margin: '4px 0 0 0',
                fontFamily: 'monospace',
              }}
            >
              Code: {errorCode}
            </p>
          )}

          {canManualRetry && (
            <button
              onClick={(event) => {
                event.stopPropagation();
                onRetry();
              }}
              style={{
                marginTop: 8,
                padding: '6px 16px',
                fontSize: 12,
                fontWeight: 600,
                color: '#fff',
                backgroundColor: '#E87A2A',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 500,
          color: config.color,
          backgroundColor: config.bgColor,
        }}
      >
        <span>{config.icon}</span>
        <span>{config.label}</span>
      </span>
      {showDetail && detail && (
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>{detail}</span>
      )}
    </div>
  );
}
